import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { useCallback, useEffect, useRef, useState } from 'react';
import { storage } from '../../lib/firebase';
import { getCachedPhotoEntry, setCachedPhotoUrl, __clearPhotoUrlStore } from './photoUrlStore';

/**
 * Storage path から HTTPS download URL を取得する hook (Phase 5 photo client 表示)。
 *
 * Sprint AL (UI1 写真表示信頼性向上, docs/16 §10-2) で discriminated union 化:
 *   - `'idle'`     : path null 等で fetch していない (初期状態 / path クリア)
 *   - `'loading'`  : fetch in flight
 *   - `'ready'`    : url 取得成功 (url + cache 保存済)
 *   - `'failed'`   : fetch エラー (attempt インクリメント済、UI 側で retry button)
 *
 * docs/106 (写真オフライン再表示強化) で **三層防御** に拡張:
 *   `usePhotoUrl(path, docUrl?)` の第 2 引数 `docUrl` は投稿 doc に保存済の download URL
 *   (`Post.photoUrl` / `Post.photoThumbUrl`)。解決優先順位:
 *     1. **localStorage cache** (photoUrlStore) を最優先 — リロード後 / オフラインでも復元可。
 *        URL の bytes は SW (CacheFirst) が保持済なので URL 文字列さえ復元できれば表示できる。
 *     2. cache miss なら **docUrl を楽観表示** (即 `ready`) — Firestore 永続キャッシュ由来でも効く。
 *     3. online で `getDownloadURL` し、成功したら cache 更新 + URL を差し替える
 *        (**stale な thumb URL を自己修復**)。失敗時は docUrl があれば維持、無ければ `failed`。
 *   - online は常に正しい URL に収束 (壊れ画像を出さない)。offline は最後に取得した URL で表示。
 *
 * 仕様 (Sprint AL から維持):
 *   - module-level Map cache (memory L0) + localStorage (永続 L1) の 2 段で path → url を保持。
 *     cache に保存するのは `getDownloadURL` 成功時のみ (失敗結果は cache しない、retry 余地を残す)。
 *   - cancelled flag で unmount 時の setState 抑止
 *   - 初回 mount で自動 fetch (attempt=1)、失敗時の自動 retry は **しない**
 *     (UI 側 button が `retry()` を呼ぶ pull 型 — 暴走 retry を防止)
 *   - `MAX_ATTEMPTS=3` 到達後の `retry()` は no-op (UI 側で固定 fallback 📷 表示へ移行)
 *   - 計装: failed transition で `console.warn('[usePhotoUrl] download failed', { path, attempt })`
 *     (docs/14 §6-5 Sentry hook 用、現状は warn のみ)
 */

export const MAX_PHOTO_URL_ATTEMPTS = 3;

/** memory L0 cache (path → URL と、保存したときの doc の URL)。localStorage (L1) より速い同期参照用。 */
const urlCache = new Map<string, { url: string; docUrl: string | undefined }>();

/**
 * memory → localStorage の順に解決 (docs/106: localStorage 最優先 = memory に無くても復元)。
 *
 * docs/28: 写真のぼかしの作り直しでは、パスは変わらずダウンロード URL (トークン) だけが変わる。
 * キャッシュを保存したときの doc の URL と今の doc の URL が違えば、キャッシュは古い写真 (ぼかす前の
 * 内容を含みうる) を指すので使わない。保存したときの doc の URL が同じ、または記録が無い (この
 * 仕組みより前に保存した) ときは、doc の URL が古いトークンでもキャッシュ (正しい URL) を使う
 * (docs/106 のサムネのトークン失効対策を保つ)。
 */
function readCache(path: string, docUrl?: string | null): string | undefined {
  let entry = urlCache.get(path);
  if (!entry) {
    entry = getCachedPhotoEntry(path);
    if (entry) urlCache.set(path, entry);
  }
  if (!entry) return undefined;
  if (docUrl && entry.url !== docUrl && entry.docUrl !== undefined && entry.docUrl !== docUrl) {
    return undefined;
  }
  return entry.url;
}

/** memory + localStorage の両方に保存 (そのときの doc の URL も一緒に覚える)。 */
function writeCache(path: string, url: string, docUrl?: string | null): void {
  urlCache.set(path, { url, docUrl: docUrl ?? undefined });
  setCachedPhotoUrl(path, url, docUrl);
}

export type PhotoUrlStatus = 'idle' | 'loading' | 'ready' | 'failed';

export interface PhotoUrlState {
  status: PhotoUrlStatus;
  /** `status==='ready'` の時のみ非 undefined。 */
  url?: string;
  /** これまでに発火した fetch 試行回数 (1-origin)。`status==='idle'` 時は 0。 */
  attempt: number;
  /** 手動 retry。`attempt >= MAX_PHOTO_URL_ATTEMPTS` または path null の時は no-op。 */
  retry: () => void;
}

/**
 * @param path  Storage path (`districts/{id}/posts/{id}/{original,thumbnail}.jpg`)。
 * @param docUrl 投稿 doc に保存済の download URL (docs/106、楽観表示 + オフライン fallback 用)。
 *   未指定 (旧呼出 / 旧投稿) の場合は従来どおり cache → getDownloadURL の 2 層解決。
 */
export function usePhotoUrl(
  path: string | null | undefined,
  docUrl?: string | null,
): PhotoUrlState {
  // path が変わる度に attempt を 0 リセットする必要があるので path を ref で track
  const lastPathRef = useRef<string | null | undefined>(path);
  // retry 起動用に「再 fetch トリガ」counter を持つ (path 不変でも increment で effect 再走)
  const [retryTick, setRetryTick] = useState(0);

  // 初期 state: cache (memory+localStorage) → docUrl 楽観 の順で初期表示を決める。
  const [state, setState] = useState<PhotoUrlState>(() => initialStateFor(path, docUrl));

  // path 変更時に state を初期化 (docUrl は path に従属するので path 変更で評価し直す)
  // docs/28: path 不変で doc の URL が変わった (ぼかしの作り直し) 場合は、下の fetch effect が
  // readCache(path, docUrl) で古いキャッシュを捨てて新しい URL を出す。
  useEffect(() => {
    if (lastPathRef.current !== path) {
      lastPathRef.current = path;
      setRetryTick(0);
      setState(initialStateFor(path, docUrl));
    }
    // docUrl は path 従属。path 不変での docUrl 変化 (trigger 後の doc 更新等) は
    // 下の fetch effect の自己修復 (getDownloadURL 成功で差替) でカバーするため、ここでは path のみ依存。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  // attempt 数を effect 外で参照するために state.attempt を ref に sync。
  // Sprint BG Part 4: react-hooks/refs (access refs during render) を回避するため useEffect で sync する。
  const attemptRef = useRef(state.attempt);
  useEffect(() => {
    attemptRef.current = state.attempt;
  }, [state.attempt]);

  const retry = useCallback(() => {
    if (!path) return;
    if (attemptRef.current >= MAX_PHOTO_URL_ATTEMPTS) return;
    // attempt インクリメント + status を 'loading' に戻す + effect 再走
    setState((prev) => ({
      status: 'loading',
      attempt: prev.attempt + 1,
      retry: noop,
    }));
    setRetryTick((t) => t + 1);
  }, [path]);

  // 実 fetch effect (online で getDownloadURL し、cache + docUrl を自己修復)
  useEffect(() => {
    if (!path) return;
    // cache hit は即時表示 (localStorage 最優先、リロード後 / オフラインでも復元、SW bytes で描画)。
    // **ただし return せず** online で background 再検証する (docs/106 §4「online は常に正しい URL に
    // 収束、壊れ画像を出さない」)。thumb は onPhotoUpload trigger で download token が回転し cached URL が
    // 失効しうる (§3-2) ため、getDownloadURL で canonical URL に収束させ stale thumb URL を自己修復する。
    // background 失敗 (offline) 時は cached 表示を維持する。
    const cached = readCache(path, docUrl);
    if (cached) {
      setState({
        status: 'ready',
        url: cached,
        attempt: Math.max(attemptRef.current, 1),
        retry: noop,
      });
    } else if (docUrl) {
      // docs/28 レビュー A1: doc の URL が変わった (ぼかしの作り直し等) ときは、getDownloadURL の
      // 結果を待たずにすぐ新しい URL を出す (電波が弱いと古い写真が長く残るため)。
      setState({
        status: 'ready',
        url: docUrl,
        attempt: Math.max(attemptRef.current, 1),
        retry: noop,
      });
    }

    // cache miss → 正しい URL に収束 (online の唯一の正)。cache hit → background 再検証。
    // docUrl があれば既に初期 state が 'ready' (楽観表示) のため、loading に戻さず裏で解決する。
    let cancelled = false;
    getDownloadURL(storageRef(storage, path))
      .then((u) => {
        if (cancelled) return;
        // 成功 = canonical URL。cache + state を fresh URL に差替 (stale thumb URL を自己修復)。
        writeCache(path, u, docUrl);
        setState({
          status: 'ready',
          url: u,
          attempt: Math.max(attemptRef.current, 1),
          retry: noop,
        });
      })
      .catch(() => {
        if (cancelled) return;
        // cache hit 済なら cached 表示を維持 (background 再検証の offline 失敗は無害、SW bytes で描画)。
        if (cached) return;
        const attempt = attemptRef.current;
        // eslint-disable-next-line no-console -- docs/14 §6-5 Sentry 未統合のため warn のみ
        console.warn('[usePhotoUrl] download failed', { path, attempt });
        // docs/106: docUrl (doc 保存 URL) があれば楽観表示を維持して offline でも写真を出す。
        // 無ければ従来どおり failed (UI で retry / 📷 fallback)。
        if (docUrl) {
          setState({ status: 'ready', url: docUrl, attempt, retry: noop });
        } else {
          setState({
            status: 'failed',
            attempt,
            retry: noop, // retry callback は下の useEffect で別途差し替える
          });
        }
      });

    return () => {
      cancelled = true;
    };
    // docUrl は path 従属で初期 state に反映済 + catch の closure で参照するため依存に含める。
  }, [path, docUrl, retryTick]);

  // status='failed' で attempt < MAX なら retry callback を露出
  // (上の fetch effect 内で setState する時点では useCallback の retry を捕まえられないので
  //  ここで二段階に分けて差し替える)
  useEffect(() => {
    if (state.status === 'failed' && state.attempt < MAX_PHOTO_URL_ATTEMPTS) {
      if (state.retry === noop) {
        setState((prev) => ({ ...prev, retry }));
      }
    }
  }, [state.status, state.attempt, state.retry, retry]);

  return state;
}

/**
 * 初期 state を path + docUrl から決定する (docs/106 三層解決の 1〜2 層目)。
 *   1. cache hit (memory/localStorage) → 即 ready
 *   2. docUrl あり → 楽観 ready (裏で getDownloadURL が自己修復)
 *   3. どちらも無し → loading (従来動作)
 */
function initialStateFor(path: string | null | undefined, docUrl?: string | null): PhotoUrlState {
  if (!path) return { status: 'idle', attempt: 0, retry: noop };
  const cached = readCache(path, docUrl);
  if (cached) return { status: 'ready', url: cached, attempt: 1, retry: noop };
  if (docUrl) return { status: 'ready', url: docUrl, attempt: 1, retry: noop };
  return { status: 'loading', attempt: 1, retry: noop };
}

function noop() {
  /* no-op retry */
}

/** test 用にキャッシュをクリア (memory + localStorage 両方)。 */
export function __clearPhotoUrlCache() {
  urlCache.clear();
  __clearPhotoUrlStore();
}
